import {
  JobServiceSubscriptionMessage,
  JobServiceSubscriptionRequest,
  JobServiceSubscriptionMessage_Type as SubscriptionMessageType,
  JobServiceSubscriptionRequest_Target as SubscriptionRequestTarget,
  jobServiceSubscriptionRequest_TargetToJSON as subscriptionRequestTargetToJSON,
  SubscriptionRequestType,
  WebSocketAuthenticateRequest,
} from "protos/rtc/web_socket";

type Listener = (message: JobServiceSubscriptionMessage) => void;

interface SubscriptionInfo {
  identifier: string;
  target: SubscriptionRequestTarget;
  type: SubscriptionMessageType;
  listeners: Set<string>;
}

const createSubscriptionKey = (
  identifier: string,
  target: SubscriptionRequestTarget,
  type: SubscriptionMessageType
): string => `${identifier}::${target}::${type}`;

export class SubscriptionManager {
  private subscriptions = new Map<string, SubscriptionInfo>();
  // keyed by a string in the format target::entityName::id e.g. job::jobId::671
  private subscriptionsByTargetKey = new Map<string, number>();

  private listenerCallbacks = new Map<string, Listener>();
  private sendMessage:
    | ((
        message: JobServiceSubscriptionRequest | WebSocketAuthenticateRequest
      ) => void)
    | undefined = undefined;
  private authenticated = false;
  private authenticating = false;
  private needsReauthentication = false;
  private connected = false;

  needsReauth(): boolean {
    return this.needsReauthentication;
  }

  authenticate(audience: string, token: string): void {
    this.setAuthenticating(true);
    this.sendAuthMessage(token, audience);
  }

  setSendMessage(
    sendFn: (
      message: JobServiceSubscriptionRequest | WebSocketAuthenticateRequest
    ) => void
  ): void {
    this.sendMessage = sendFn;
  }

  setConnected(connected: boolean): void {
    this.connected = connected;
    if (!connected) {
      // Don't reset everything, just mark as needing re-auth
      this.setAuthenticated(false);
      this.needsReauthentication = true;
      // Keep subscriptions and other state
    } else if (!this.isAuthenticated() && !this.isAuthenticating()) {
      // Trigger re-authentication on reconnection
      this.needsReauthentication = true;
    }
  }

  isConnected(): boolean {
    return this.connected;
  }

  setAuthenticating(value: boolean): void {
    this.authenticating = value;
  }

  isAuthenticating(): boolean {
    return this.authenticating;
  }

  isAuthenticated(): boolean {
    return this.authenticated;
  }

  private getTargetKey(info: SubscriptionInfo): string {
    // https://carbonrobotics.atlassian.net/wiki/spaces/Engineering/pages/930545715/AutoTractor+Jobs+Service#WebSocket-API
    // There's a table at the bottom of this page explaining what is available
    //
    // TASK taskId
    // JOB jobId
    // OBJECTIVE objectiveId
    // ACTIVE_TASK robotSerial (not a subscription topic)
    // ACTIVE_OBJECTIVE robotSerial (not a subscription topic)
    // CHECKLIST robotSerial (not a subscription topic)
    // CHECKLIST_EXEMPTION robotSerial (not a subscription topic)
    // ROBOT robotSerial
    // JOBCONFIG jobId
    // Interventions <none>
    // Interventions robotSerial
    const getSelector = (): string => {
      switch (info.type) {
        case SubscriptionMessageType.JOB:
        case SubscriptionMessageType.JOB_CONFIG: {
          return "jobId";
        }
        case SubscriptionMessageType.OBJECTIVE:
        case SubscriptionMessageType.OBJECTIVE_ASSIGNMENT: {
          return "objectiveId";
        }
        case SubscriptionMessageType.CHECKLIST:
        case SubscriptionMessageType.CHECKLIST_EXEMPTION:
        case SubscriptionMessageType.ACTIVE_ATOMIC_TASK:
        case SubscriptionMessageType.ACTIVE_OBJECTIVE: {
          return "robotSerial";
        }
        case SubscriptionMessageType.TASK: {
          return "taskId";
        }
        case SubscriptionMessageType.INTERVENTION: {
          // robot interventions go to their own topic, not the robot's
          if (info.target === SubscriptionRequestTarget.ROBOT) {
            return "interventionsRobotSerial";
          }
          return "interventions";
        }
      }
      return "none";
    };

    const selector = getSelector();
    return `${info.target}::${selector}::${info.identifier}`;
  }

  public setAuthenticated(value: boolean): void {
    this.authenticated = value;
    this.authenticating = false;

    // subscribe to everything we asked for right after authenticating
    if (value && this.sendMessage) {
      for (const [_key, info] of this.subscriptions) {
        const targetKey = this.getTargetKey(info);
        if (this.subscriptionsByTargetKey.get(targetKey)) {
          this.sendSubscribeMessage(info);
        }
      }
    }
  }

  subscribe(
    identifier: string,
    target: SubscriptionRequestTarget,
    type: SubscriptionMessageType,
    listenerId: string,
    callback: Listener
  ): void {
    const key = createSubscriptionKey(identifier, target, type);
    this.listenerCallbacks.set(listenerId, callback);
    let info = this.subscriptions.get(key);

    if (!info) {
      info = {
        identifier,
        target,
        type,
        listeners: new Set(),
      };
      this.subscriptions.set(key, info);
    }

    // refcount is dependent on X, Y. Subscriptions are unique by X. An X, Z and X, Y subscription result in duplicates.
    if (!info.listeners.has(listenerId)) {
      info.listeners.add(listenerId);
      this.subscriptionsByTargetKey.set(
        this.getTargetKey(info),
        (this.subscriptionsByTargetKey.get(this.getTargetKey(info)) ?? 0) + 1
      );

      // subscribe if it's the first subscriber.
      if (
        (this.subscriptionsByTargetKey.get(this.getTargetKey(info)) ?? 0) ===
          1 &&
        this.sendMessage
      ) {
        this.sendSubscribeMessage(info);
      }
    }
  }

  unsubscribe(
    identifier: string,
    target: SubscriptionRequestTarget,
    type: SubscriptionMessageType,
    listenerId: string
  ): void {
    const key = createSubscriptionKey(identifier, target, type);
    this.listenerCallbacks.delete(listenerId);
    const info = this.subscriptions.get(key);

    if (!info || !info.listeners.has(listenerId)) {
      return;
    }
    info.listeners.delete(listenerId);
    this.subscriptionsByTargetKey.set(
      this.getTargetKey(info),
      (this.subscriptionsByTargetKey.get(this.getTargetKey(info)) ?? 1) - 1
    );

    if (!this.subscriptionsByTargetKey.get(this.getTargetKey(info))) {
      // unsubscribe if no one wants it anymore
      if (this.isAuthenticated() && this.sendMessage) {
        this.sendUnsubscribeMessage(info);
      }
      this.subscriptions.delete(key);
    }
  }

  unsubscribeAll(listenerId: string): void {
    const toUnsubscribe: Array<
      [string, SubscriptionRequestTarget, SubscriptionMessageType]
    > = [];

    for (const info of this.subscriptions.values()) {
      if (info.listeners.has(listenerId)) {
        toUnsubscribe.push([info.identifier, info.target, info.type]);
      }
    }

    for (const [identifier, target, type] of toUnsubscribe) {
      this.unsubscribe(identifier, target, type, listenerId);
    }

    this.listenerCallbacks.delete(listenerId);
  }

  handleMessage(message: JobServiceSubscriptionMessage): void {
    for (const info of this.subscriptions.values()) {
      if (this.messageMatchesSubscription(message, info)) {
        for (const listenerId of info.listeners) {
          this.listenerCallbacks.get(listenerId)?.(message);
        }
      }
    }
  }

  private messageMatchesSubscription(
    message: JobServiceSubscriptionMessage,
    info: SubscriptionInfo
  ): boolean {
    if (info.type !== message.type) {
      return false;
    }

    const interestSet: {
      robotSerial?: string;
      jobId?: string;
      objectiveId?: string;
      taskId?: string;
    } = {};

    // check message for well-formedness and extract interest set
    switch (message.type) {
      case SubscriptionMessageType.ACTIVE_OBJECTIVE: {
        if (!message.activeObjective) {
          return false;
        }
        // active objective messages are always interesting; they don't provide
        // a robot serial to scope on
        break;
      }
      case SubscriptionMessageType.ACTIVE_ATOMIC_TASK: {
        if (!message.activeAtomicTask) {
          return false;
        }
        // active atomic task messages are always interesting; they don't
        // provide a robot serial to scope on
        break;
      }
      case SubscriptionMessageType.CHECKLIST: {
        if (!message.checklist) {
          return false;
        }
        interestSet.robotSerial = message.checklist.checklist?.robotSerial;
        break;
      }
      case SubscriptionMessageType.CHECKLIST_EXEMPTION: {
        if (!message.checklistExemption) {
          return false;
        }
        interestSet.robotSerial =
          message.checklistExemption.checklistExemption?.robotSerial;
        break;
      }
      case SubscriptionMessageType.JOB_CONFIG: {
        if (!message.jobConfig) {
          return false;
        }

        interestSet.jobId = message.jobConfig.configuration?.jobId.toString();
        break;
      }
      case SubscriptionMessageType.JOB: {
        if (!message.job) {
          return false;
        }

        interestSet.jobId = message.job.job?.id.toString();
        break;
      }
      case SubscriptionMessageType.OBJECTIVE: {
        if (!message.objective) {
          return false;
        }

        interestSet.objectiveId = message.objective.objective?.id.toString();
        interestSet.jobId = message.objective.objective?.jobId.toString();
        break;
      }
      case SubscriptionMessageType.TASK: {
        if (!message.task) {
          return false;
        }

        interestSet.objectiveId = message.task.task?.objectiveId.toString();
        interestSet.taskId = message.task.task?.id.toString();
        break;
      }
      case SubscriptionMessageType.INTERVENTION: {
        if (!message.intervention) {
          return false;
        }

        interestSet.robotSerial =
          message.intervention.intervention?.robotSerial;
        interestSet.jobId = message.intervention.intervention?.jobId.toString();
        break;
      }
      case SubscriptionMessageType.OBJECTIVE_ASSIGNMENT: {
        if (!message.objectiveAssignment) {
          return false;
        }

        interestSet.robotSerial =
          message.objectiveAssignment.objectiveAssignment?.robotSerial;
        interestSet.objectiveId =
          message.objectiveAssignment.objectiveAssignment?.objectiveId.toString();
        break;
      }
    }

    // match interest sets to subscription target
    switch (info.target) {
      case SubscriptionRequestTarget.INTERVENTIONS: {
        // currently there's no way to scope down your subscription to specific
        // interventions; assume all of the messages you get are relevant
        return true;
      }
      case SubscriptionRequestTarget.ROBOT: {
        if (
          info.type === SubscriptionMessageType.ACTIVE_ATOMIC_TASK ||
          info.type === SubscriptionMessageType.ACTIVE_OBJECTIVE
        ) {
          // active atomic task and active objective messages don't provide
          // a robot serial to scope on, so assume they're relevant to our
          // robot subscription
          return true;
        }

        return info.identifier === interestSet.robotSerial;
      }
      case SubscriptionRequestTarget.JOB:
      case SubscriptionRequestTarget.JOB_CONFIG: {
        return info.identifier === interestSet.jobId;
      }
      case SubscriptionRequestTarget.OBJECTIVE: {
        return info.identifier === interestSet.objectiveId;
      }
      case SubscriptionRequestTarget.TASK: {
        return info.identifier === interestSet.taskId;
      }
      default: {
        return false;
      }
    }
  }

  private sendAuthMessage(token: string, audience: string): void {
    this.sendMessage?.({ token, audience });
  }

  private sendSubscribeMessage(info: SubscriptionInfo): void {
    if (!this.sendMessage) {
      return;
    }

    const params = buildSubscriptionMessage(
      info,
      SubscriptionRequestType.SUBSCRIBE
    );
    if (params) {
      this.sendMessage(params);
    }
  }

  private sendUnsubscribeMessage(info: SubscriptionInfo): void {
    if (!this.sendMessage) {
      return;
    }

    const params = buildSubscriptionMessage(
      info,
      SubscriptionRequestType.UNSUBSCRIBE
    );
    if (params) {
      this.sendMessage(params);
    }
  }

  reset(): void {
    this.setAuthenticated(false);
    this.setAuthenticating(false);
    this.sendMessage = undefined;
  }
}

const VALID_MESSAGE_TYPES_FOR_TARGET: {
  [target in SubscriptionRequestTarget]: SubscriptionMessageType[];
} = {
  [SubscriptionRequestTarget.UNRECOGNIZED]: [],
  [SubscriptionRequestTarget.TARGET_UNSPECIFIED]: [],
  [SubscriptionRequestTarget.JOB_CONFIG]: [SubscriptionMessageType.JOB_CONFIG],
  [SubscriptionRequestTarget.ROBOT]: [
    SubscriptionMessageType.INTERVENTION,
    SubscriptionMessageType.ACTIVE_ATOMIC_TASK,
    SubscriptionMessageType.ACTIVE_OBJECTIVE,
    SubscriptionMessageType.OBJECTIVE_ASSIGNMENT,
    SubscriptionMessageType.CHECKLIST,
    SubscriptionMessageType.CHECKLIST_EXEMPTION,
  ],
  [SubscriptionRequestTarget.OBJECTIVE]: [
    SubscriptionMessageType.OBJECTIVE,
    SubscriptionMessageType.OBJECTIVE_ASSIGNMENT,
    SubscriptionMessageType.TASK,
  ],
  [SubscriptionRequestTarget.TASK]: [SubscriptionMessageType.TASK],
  [SubscriptionRequestTarget.JOB]: [
    SubscriptionMessageType.JOB,
    SubscriptionMessageType.OBJECTIVE,
  ],
  [SubscriptionRequestTarget.INTERVENTIONS]: [
    SubscriptionMessageType.INTERVENTION,
  ],
  [SubscriptionRequestTarget.INTERVENER_SESSION]: [
    SubscriptionMessageType.INTERVENER_SESSION,
  ],
};

const buildSubscriptionMessage = (
  info: SubscriptionInfo,
  subscriptionType: SubscriptionRequestType
): JobServiceSubscriptionRequest | undefined => {
  if (!VALID_MESSAGE_TYPES_FOR_TARGET[info.target].includes(info.type)) {
    console.error(
      "invalid subscription combination",
      info,
      info.target,
      info.type
    );
    return;
  }
  const params: JobServiceSubscriptionRequest = {
    type: subscriptionType,
    target: info.target,
  };
  switch (info.target) {
    case SubscriptionRequestTarget.ROBOT: {
      if (info.type === SubscriptionMessageType.INTERVENTION) {
        params.target = SubscriptionRequestTarget.INTERVENTIONS;
      }
      params.robotSerial = info.identifier;
      break;
    }
    case SubscriptionRequestTarget.JOB:
    case SubscriptionRequestTarget.JOB_CONFIG: {
      params.jobId = Number.parseInt(info.identifier);
      break;
    }
    case SubscriptionRequestTarget.OBJECTIVE: {
      params.objectiveId = Number.parseInt(info.identifier);
      break;
    }
    case SubscriptionRequestTarget.TASK: {
      params.taskId = Number.parseInt(info.identifier);
      break;
    }
    default: {
      console.warn(
        "buildSubscriptionMessage: unexpected target",
        subscriptionRequestTargetToJSON(info.target)
      );
      break;
    }
  }

  return params;
};
